import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronRight } from 'lucide-react';
import type { AppContextMenuItem, AppContextMenuSubItem, AppContextMenuState } from './buildAppContextMenu';
import { clampMenuPosition } from './buildAppContextMenu';

export type { AppContextMenuState } from './buildAppContextMenu';

const MENU_WIDTH = 240;
const MENU_ITEM_HEIGHT = 36;
const SUBMENU_WIDTH = 200;
/** Invisible hover bridge between parent row and flyout (prevents accidental close). */
const SUBMENU_BRIDGE_PX = 8;
const SUBMENU_CLOSE_DELAY_MS = 120;

interface AppContextMenuProps {
  state: AppContextMenuState | null;
  items: AppContextMenuItem[];
  onClose: () => void;
}

export function AppContextMenu({ state, items, onClose }: AppContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const submenuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null);
  const [submenuFlipLeft, setSubmenuFlipLeft] = useState(false);

  const cancelSubmenuClose = () => {
    if (submenuCloseTimerRef.current) {
      clearTimeout(submenuCloseTimerRef.current);
      submenuCloseTimerRef.current = null;
    }
  };

  const scheduleSubmenuClose = () => {
    cancelSubmenuClose();
    submenuCloseTimerRef.current = setTimeout(() => {
      setOpenSubmenuId(null);
      submenuCloseTimerRef.current = null;
    }, SUBMENU_CLOSE_DELAY_MS);
  };

  const openSubmenu = (submenuId: string, el: HTMLDivElement | null) => {
    cancelSubmenuClose();
    setOpenSubmenuId(submenuId);
    if (el) {
      const rect = el.getBoundingClientRect();
      const bridge = SUBMENU_BRIDGE_PX;
      setSubmenuFlipLeft(rect.right + bridge + SUBMENU_WIDTH > window.innerWidth - 8);
    }
  };

  useEffect(() => {
    if (!state?.open) {
      cancelSubmenuClose();
      setOpenSubmenuId(null);
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const handleScroll = () => onClose();
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('mousedown', handleClickOutside);
      cancelSubmenuClose();
    };
  }, [state?.open, onClose]);

  if (!state?.open) return null;

  const estimatedHeight = items.reduce((h, item) => {
    if (item.type === 'divider') return h + 9;
    return h + MENU_ITEM_HEIGHT;
  }, 16);

  const { x, y } = clampMenuPosition(state.x, state.y, MENU_WIDTH, estimatedHeight);

  const runAction = (action: () => void) => {
    action();
    onClose();
  };

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      className="app-context-menu fixed z-[200] rounded-xl py-1 text-sm overflow-visible"
      style={{ top: y, left: x, width: MENU_WIDTH }}
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item) => {
        if (item.type === 'divider') {
          return <div key={item.id} className="app-context-menu-divider h-px my-1" role="separator" />;
        }

        if (item.type === 'submenu') {
          return (
            <SubmenuRow
              key={item.id}
              item={item}
              isOpen={openSubmenuId === item.id}
              flipLeft={submenuFlipLeft}
              onEnter={(el) => openSubmenu(item.id, el)}
              onLeave={scheduleSubmenuClose}
              onFlyoutEnter={cancelSubmenuClose}
              onSelect={(action) => runAction(action)}
            />
          );
        }

        const Icon = item.icon;
        return (
          <button
            key={item.id}
            type="button"
            role="menuitem"
            className="app-context-menu-item w-full text-left px-4 py-2 flex items-center gap-2"
            onClick={() => runAction(item.onClick)}
          >
            {Icon ? <Icon className="app-context-menu-item-icon w-4 h-4 shrink-0" /> : <span className="w-4 shrink-0" />}
            <span className="flex-1 truncate">{item.label}</span>
            {item.shortcut ? (
              <span className="app-context-menu-item-shortcut text-xs ml-2 shrink-0">{item.shortcut}</span>
            ) : null}
          </button>
        );
      })}
    </div>,
    document.body,
  );
}

function SubmenuRow({
  item,
  isOpen,
  flipLeft,
  onEnter,
  onLeave,
  onFlyoutEnter,
  onSelect,
}: {
  item: Extract<AppContextMenuItem, { type: 'submenu' }>;
  isOpen: boolean;
  flipLeft: boolean;
  onEnter: (el: HTMLDivElement | null) => void;
  onLeave: () => void;
  onFlyoutEnter: () => void;
  onSelect: (action: () => void) => void;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  const Icon = item.icon;
  const bridge = SUBMENU_BRIDGE_PX;

  return (
    <div
      ref={rowRef}
      className="relative group/submenu"
      onMouseEnter={() => onEnter(rowRef.current)}
      onMouseLeave={onLeave}
    >
      <button
        type="button"
        role="menuitem"
        aria-haspopup="true"
        aria-expanded={isOpen}
        className="app-context-menu-item w-full text-left px-4 py-2 flex items-center gap-2"
      >
        {Icon ? <Icon className="app-context-menu-item-icon w-4 h-4 shrink-0" /> : <span className="w-4 shrink-0" />}
        <span className="flex-1 truncate">{item.label}</span>
        <ChevronRight className="app-context-menu-item-chevron w-4 h-4 shrink-0" />
      </button>

      {isOpen ? (
        <div
          role="presentation"
          className="absolute top-0 bottom-0 z-[201] min-h-full"
          style={
            flipLeft
              ? { right: '100%', width: SUBMENU_WIDTH + bridge, paddingRight: bridge }
              : { left: '100%', width: SUBMENU_WIDTH + bridge, paddingLeft: bridge }
          }
          onMouseEnter={onFlyoutEnter}
          onMouseLeave={onLeave}
        >
          <div
            role="menu"
            className="app-context-menu rounded-xl py-1"
            style={{ width: SUBMENU_WIDTH }}
          >
            {item.items.map((sub: AppContextMenuSubItem) => (
              <button
                key={sub.id}
                type="button"
                role="menuitem"
                className="app-context-menu-item w-full text-left px-4 py-2 flex items-center gap-2"
                onClick={() => onSelect(sub.onClick)}
              >
                <span className="w-4 h-4 shrink-0 flex items-center justify-center">
                  {sub.checked ? <Check className="app-context-menu-check w-3.5 h-3.5" /> : null}
                </span>
                <span className="flex-1 truncate">{sub.label}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default AppContextMenu;
